import { SxProps, Theme } from '@mui/material';

/**
 * Interaction forms render on surfaces with very different input methods: a
 * mouse-driven desktop panel and a touch-driven mobile bottom sheet. Both use
 * the same colour and type vocabulary — only density differs — so the two
 * presets below are the single place that difference is expressed.
 *
 * What comes from where:
 * - **Colours** are always theme token *names* (`v4.border`, `text.primary`,
 *   `background.paper`), resolved by MUI. Never put a raw hex in this module.
 * - **Gaps** are theme spacing units, so `1.5` is `theme.spacing(1.5)`.
 * - **Control height and font** inside fields come from the global `FIELD_*`
 *   overrides in `src/configs/themes.ts` — this module does not restate them.
 *   `minControlHeight` is the one exception, and only raises a floor.
 * - **The font sizes below are literals**, matching the existing convention for
 *   shared sx constants (see `SECTION_LABEL_SX` in
 *   `src/pages/Services/CreateService/constants.ts`). The theme defines no
 *   typography variants for these roles, so there is nothing to derive from.
 */
export enum InteractionFormDensity {
  /** Desktop panels and dialogs — matches the Create Service field rhythm. */
  Compact = 'compact',
  /** Mobile bottom sheets — larger type and 44px minimum touch targets. */
  Touch = 'touch',
}

export interface InteractionFormDensityTokens {
  /** Padding inside the form card. */
  cardPadding: string;
  /** Theme spacing units between fields within a card. */
  fieldGap: number;
  /** Theme spacing units between the card header and the first field. */
  headerGap: number;
  formTitleFontSize: string;
  fieldLabelFontSize: string;
  helperFontSize: string;
  /**
   * Minimum height applied to controls inside a field block. `undefined` leaves
   * the theme's own field density alone (desktop is already tuned in
   * `themes.ts` via FIELD_INPUT_PADDING_Y).
   */
  minControlHeight?: number;
}

/**
 * Touch-target floor. 44px is this project's rule (enforced by
 * `/ui-security-style-review`) and matches WCAG 2.1 **SC 2.5.5 Target Size,
 * Level AAA** — it is deliberately stricter than the AA bar, which is WCAG 2.2
 * SC 2.5.8 Target Size (Minimum) at 24px.
 */
const TOUCH_TARGET_MIN_PX = 44;

const COMPACT_TOKENS: InteractionFormDensityTokens = {
  cardPadding: '12px 14px',
  fieldGap: 1.5,
  headerGap: 1,
  formTitleFontSize: '13px',
  fieldLabelFontSize: '13px',
  helperFontSize: '12px',
};

const TOUCH_TOKENS: InteractionFormDensityTokens = {
  cardPadding: '16px',
  fieldGap: 2,
  headerGap: 1.5,
  formTitleFontSize: '15px',
  fieldLabelFontSize: '13px',
  helperFontSize: '12px',
  minControlHeight: TOUCH_TARGET_MIN_PX,
};

export const getInteractionFormTokens = (
  density: InteractionFormDensity
): InteractionFormDensityTokens =>
  density === InteractionFormDensity.Touch ? TOUCH_TOKENS : COMPACT_TOKENS;

/** The form card surface, shared by every interaction-form consumer. */
export const interactionCardSx = (tokens: InteractionFormDensityTokens): SxProps<Theme> => ({
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'v4.border',
  borderRadius: 2,
  p: tokens.cardPadding,
});

/** Vertical rhythm for the fields inside a card. */
export const interactionFieldStackSx = (tokens: InteractionFormDensityTokens): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.fieldGap,
});
